import type { InventoryOp, SkillId } from "@rietz/shared";

/**
 * The UI → game half of the bridge (`ui/store.ts` is the other). Components
 * never import the socket or `game/`; they call one of these, and the game
 * registers the implementation at boot via `setUiActions` — until then every
 * call is a silent no-op. No return values: results come back through store
 * setters.
 */
export interface UiActions {
  /**
   * One inventory edit, applied optimistically through the shared
   * `applyInventoryOp`, pushed into the store and sent as `invOp`; a failed
   * `invResult` rolls back. WHERE it applies is `main.ts`'s routing (raid or
   * menu) by which screen is up.
   */
  tryOp(op: InventoryOp): void;
  /** Start using a med (`useMed` message). Target limb is server-resolved. */
  useMed(iid: string): void;
  /** Close the open world container (sends `close`, clears the store slot). */
  closeContainer(): void;
  /**
   * Open or close the inventory screen. The game's because it is an INPUT-MODE
   * change: the pointer must be released and re-locked.
   */
  setInventoryOpen(open: boolean): void;
  /**
   * Open or close the in-raid settings overlay — the game's for the same
   * pointer reason. The MENU's settings tab needs none of this.
   */
  setSettingsOpen(open: boolean): void;

  // --- Meta / lobby: same contract — no return values. What happens next
  // arrives through the store (`queueStatus`, `welcome`, `profile`).

  /** "Escape from Rietz": join the queue for the next raid (`queueJoin`). */
  deploy(): void;
  /** Take me out of the queue again (`queueLeave`). */
  leaveQueue(): void;
  /**
   * Buy one offer off a trader's shelf. Not predicted: euros and the minted
   * instance come back in the `profile`.
   */
  buy(traderId: string, defId: string): void;
  /** Sell one instance out of the STASH. Same non-optimism as `buy`. */
  sell(traderId: string, iid: string): void;
  /** Repair one armor instance, wherever it is. Trader-less, and menu-only. */
  repair(iid: string): void;
  /**
   * Hand the named instances over against ONE `bringItems` quest — the only way
   * a fetch quest's counter moves. `iids` in consumption order. Not predicted.
   */
  questTurnIn(questId: string, iids: string[]): void;

  // --- The hideout: six more of the same. The screen greys out what the shared
  // `buildProblem` / `craftProblem` would refuse.

  /** Start the next level of a module. Costs leave the account immediately. */
  hideoutBuild(moduleId: string): void;
  /** Start a recipe. The inputs go now, the output arrives on collection. */
  hideoutCraft(craftId: string): void;
  /** Take a FINISHED craft's output into the stash. Can fail on space. */
  hideoutCollect(craftId: string): void;
  /** Empty a production module's buffer into the stash. Can fail on space. */
  hideoutHarvest(moduleId: string): void;
  /** Feed ONE canister — a stash instance, by id — into the generator's tank. */
  hideoutFuel(iid: string): void;
  /** The generator's switch. `false` stops every powered craft with it. */
  hideoutPower(on: boolean): void;

  // --- The walkable shelter: the hideout SCREEN's half of the bargain
  // `setInventoryOpen` struck — walking the cellar is an INPUT MODE.

  /**
   * Leave the menu and walk into the shelter (screen `"hideout"`). The game's:
   * the pointer is TAKEN and the little world built. Meta servers only.
   */
  enterHideout(): void;
  /** Walk back out to the menu: the world stops, the pointer goes back to the
   *  DOM, and the menu comes up where it was. */
  leaveHideout(): void;
  /**
   * Close whatever the shelter has open — a station's panel or the gym overlay.
   * The game's, because closing means taking the pointer back.
   */
  closeHideoutOverlay(): void;
  /**
   * One finished gym session: the skill and the score in [0, 1]
   * (`hideoutTrain`). Not predicted — the gain and the cooldown are the
   * server's.
   */
  hideoutTrain(skill: SkillId, score: number): void;

  // --- Friends and the party: seven more, and none of them draws anything —
  // what comes back is the whole state (`friends` / `partyState`) or a refusal.
  // Everyone is named by USERNAME.

  /** Ask `username` to be friends. */
  friendRequest(username: string): void;
  /** Answer an incoming request: accept it, or decline it silently. */
  friendRespond(username: string, accept: boolean): void;
  /** Unfriend, or withdraw a request we sent. One gesture, one verb. */
  friendRemove(username: string): void;
  /** Ask a friend into the squad, creating one with us as leader if needed. */
  partyInvite(username: string): void;
  /** Answer THE pending invite — there is never more than one. */
  partyRespond(accept: boolean): void;
  /** Walk out of the squad. Leadership passes on; it dies with its last member. */
  partyLeave(): void;
  /** Throw a member out. Leader only. */
  partyKick(username: string): void;

  // --- The ladder (protocol v25) -------------------------------------------

  /**
   * Ask the server for the leaderboard (`GET /leaderboard`) — the one action
   * that is not a WebSocket message, keeping the contract anyway: the answer
   * arrives via `setLeaderboard`, and the URL stays the game's business. Called
   * on the profile tab's mount; a failed fetch is a no-op.
   */
  fetchLeaderboard(): void;

  /** Revoke the database-backed browser session and return to the login screen. */
  logout(): void;
  /**
   * "Back to menu" on the raid-end screen: tears the finished raid down and
   * returns to the menu.
   */
  closeSummary(): void;
}

/** Every action a no-op: the pre-boot / test default. */
const NOOP_ACTIONS: UiActions = {
  tryOp: () => undefined,
  useMed: () => undefined,
  closeContainer: () => undefined,
  setInventoryOpen: () => undefined,
  setSettingsOpen: () => undefined,
  deploy: () => undefined,
  leaveQueue: () => undefined,
  buy: () => undefined,
  sell: () => undefined,
  repair: () => undefined,
  questTurnIn: () => undefined,
  hideoutBuild: () => undefined,
  hideoutCraft: () => undefined,
  hideoutCollect: () => undefined,
  hideoutHarvest: () => undefined,
  hideoutFuel: () => undefined,
  hideoutPower: () => undefined,
  enterHideout: () => undefined,
  leaveHideout: () => undefined,
  closeHideoutOverlay: () => undefined,
  hideoutTrain: () => undefined,
  friendRequest: () => undefined,
  friendRespond: () => undefined,
  friendRemove: () => undefined,
  partyInvite: () => undefined,
  partyRespond: () => undefined,
  partyLeave: () => undefined,
  partyKick: () => undefined,
  fetchLeaderboard: () => undefined,
  logout: () => undefined,
  closeSummary: () => undefined,
};

let current: UiActions = NOOP_ACTIONS;

/** Registers the game-side implementation. Called once from game boot. */
export function setUiActions(actions: UiActions): void {
  current = actions;
}

/** The registered actions. Components call this at EVENT time, never at render
 *  time, so registration order relative to mount does not matter. */
export function uiActions(): UiActions {
  return current;
}
